import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setTeachersBalances, confirmTransferMoney, processReportFile } from '../../actions/bank';
import { useTranslation } from 'react-i18next';
import usePhoneCheck from '../../hooks/usePhoneCheck';
import BankMobileBody from '../../components/ComponentTable/Table/BankMobileBody';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import BankTableBody from '../../components/ComponentTable/Table/BankTableBody';
import BankTableHead from '../../components/ComponentTable/Table/BankTableHead';
import BankTableToolbar from '../../components/ComponentTable/Table/BankTableToolbar';
import { Stack, Box, Table, TableContainer, Paper, Button } from '@mui/material';
import BackButton from '../../components/BalanceComponent/BackButton';
import { showCurrentStatus } from '../../actions/user';
import { filterData } from '../../actions/filterData';
import { toast } from 'react-toastify';
import BankReportService from '../../services/bank-report.service';
import { STATUSES } from '../../constants/statuses';

const headCells = [
    {
        id: 'checkbox',
        numeric: false,
        disablePadding: false,
        label: '',
        type: 'checkbox',
    },
    {
        id: 'rowId',
        numeric: false,
        disablePadding: false,
        label: 'id',
        type: 'string',
    },
    {
        id: 'surname',
        numeric: false,
        disablePadding: false,
        label: 'bankTeachersPage.surname',
        type: 'string',
    },
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'bankTeachersPage.name',
        type: 'string',
    },
    {
        id: 'balance',
        numeric: false,
        disablePadding: false,
        label: 'bankTeachersPage.balance',
        type: 'string',
    },
    {
        id: 'studentBalances',
        numeric: false,
        disablePadding: false,
        label: 'bankTeachersPage.studentBalances',
        type: 'string',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'bankTeachersPage.status',
        type: 'status',
    },
    {
        id: 'quantity',
        numeric: true,
        disablePadding: false,
        label: 'bankTeachersPage.quantity',
        type: 'decimalInput',
    },
    {
        id: 'bankAccount',
        numeric: true,
        disablePadding: false,
        label: 'bankTeachersPage.bankAccount',
        type: 'link',
    },
];

function getRowData(data) {
    return data
        ? data.map(item => ({
              error: false,
              rowId: item.teacher_id,
              name: item.teacher_name.split(' ').pop() || '',
              surname: item.teacher_name.split(' ')[0] || '',
              balance: item.teacher_balance || '',
              studentBalances: item.students_balances || '',
              quantity: 100,
              status: item.teacher_status,
          }))
        : [];
}

export default function BankTeachersPage() {
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorStates, setErrorStates] = useState({});
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [filters, setFilters] = useState({
        rowId: '',
        surname: '',
        status: '',
    });

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const history = useHistory();
    const { t, i18n } = useTranslation();
    const user = useSelector(state => state.bank);
    const balance = user.customer.balance || 0;

    const teacherBalancesInfo = useMemo(() => {
        return getRowData(user.customer.teachersBalances);
    }, [user]);
    const mobile = usePhoneCheck();

    useEffect(() => {
        setRows(teacherBalancesInfo);
    }, [teacherBalancesInfo]);

    const [rows, setRows] = useState(teacherBalancesInfo);
    const dispatch = useDispatch();

    const fetchTeachersBalances = async () => {
        await dispatch(setTeachersBalances());
    };

    useEffect(() => {
        fetchTeachersBalances();
    }, []);

    const handleQuantityChange = (id, value) => {
        const rowIndex = rows.findIndex(row => row.rowId === id);
        const newRows = [...rows];
        newRows[rowIndex] = {
            ...newRows[rowIndex],
            quantity: parseFloat(value) || '',
        };

        setRows(newRows);
        setErrorStates(prevState => ({
            ...prevState,
            [id]: value === '',
        }));
    };

    const handleSelectAllClick = event => {
        if (loading) return;
        if (event.target.checked) {
            const newSelected = rows.map(n => n.rowId);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        if (loading) return;

        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = [...selected, id];
        } else if (selectedIndex >= 0) {
            newSelected = selected.filter(itemId => itemId !== id);
        }

        setSelected(newSelected);
        setErrorStates(prevState => ({
            ...prevState,
            [id]: false,
        }));
    };

    const isSelected = id => selected.indexOf(id) !== -1;

    async function handleSuccessClick() {
        await confirmTransferMoney({
            rows,
            isSelected,
            toast,
            t,
            balance,
            updateBalanceFunction: fetchTeachersBalances,
            setSelected,
        });
    }

    const handleFilterChange = (key, value) => {
        if (loading) return;
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setRows(filterData(teacherBalancesInfo, newFilters, status => showCurrentStatus(status, { t, i18n })));
    };

    const filterConfigs = [
        { label: 'ID', key: 'rowId' },
        { label: t('bankTeachersPage.teacherSurname'), key: 'surname' },
        { label: t('bankTeachersPage.status'), key: 'status', transform: val => showCurrentStatus(val, { t, i18n }) },
    ];

    const handleBankAccountClick = id => {
        history.push(`/users/bank/${id}/teachers`);
    };

    async function downloadDocument() {
        try {
            const params = {};

            if (filters.status) {
                const rawStatus = STATUSES
                    .find(item => t(`users.${item.title}`) === filters.status)?.id;
                if (rawStatus !== undefined) {
                    params.status = rawStatus;
                }
            }

            if (selected.length) {
                const visibleIds = new Set(rows.map(row => row.rowId));
                const filteredSelected = selected.filter(id => visibleIds.has(id));
                if (filteredSelected.length) {
                    params.ids = filteredSelected.join(',');
                }
            }

            Object.keys(filters).forEach(field => {
                if (!(field in params) && filters[field]) {
                    params[field] = filters[field];
                }
            });

            const response = await BankReportService.GetTeachersReport(params);
        
            if (!response || !response.payload || !response.payload.fileName) {
                return;
            }

            await processReportFile(response);
        
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <BackButton />
            <Paper sx={{ width: '100%', mb: 2, p: 3 }}>
                <Button
                    variant="contained"
                    onClick={downloadDocument}
                    style={{
                        padding: '5px',
                        borderRadius: '4px',
                        marginBottom: '15px',
                    }}
                >
                    {`${t('buttons.download_csv')} Excel`}
                </Button>
                <Stack direction={mobile ? 'column' : 'row'} justifyContent="flex-start" spacing={2}>
                    {filterConfigs.map(({ label, key, transform }) => (
                        <CustomAutocomplete
                            key={key}
                            label={label}
                            options={[...new Set(rows.map(row => (transform ? transform(row[key]) : row[key]) || ''))]}
                            inputValue={filters[key] || ''}
                            onChange={(e, newValue) => handleFilterChange(key, newValue || '')}
                            onInputChange={(e, newInputValue) =>
                                setFilters(prev => ({ ...prev, [key]: newInputValue || '' }))
                            }
                        />
                    ))}
                </Stack>
                <BankTableToolbar
                    title={t('bankTeachersPage.selectedTeachers')}
                    titleTooltipSuccess={t('bankStudentsPage.tooltipTitle')}
                    numSelected={selected.length}
                    onSuccessClick={handleSuccessClick}
                    loading={loading}
                    selectedUserIds={selected}
                    rows={rows}
                />
                {mobile ? (
                    <BankMobileBody
                        rows={rows}
                        headCells={headCells}
                        isSelected={isSelected}
                        handleSelectAllClick={handleSelectAllClick}
                        handleQuantityChange={handleQuantityChange}
                        handleClick={handleClick}
                        showCurrentStatus={showCurrentStatus}
                        handleBankAccountClick={handleBankAccountClick}
                        mobile={mobile}
                    />
                ) : (
                    <TableContainer style={{ maxHeight: '59vh' }}>
                        <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                            <BankTableHead
                                numSelected={selected.length}
                                onSelectAllClick={handleSelectAllClick}
                                headCells={headCells}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                            />
                            <BankTableBody
                                rows={rows}
                                headCells={headCells}
                                isSelected={isSelected}
                                handleClick={handleClick}
                                handleQuantityChange={handleQuantityChange}
                                handleBankAccountClick={handleBankAccountClick}
                                order={order}
                                orderBy={orderBy}
                            />
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}
